import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LayoutWithSidebar from "../components/layouts/LayoutWithSidebar";
import ProtectedRoute from "./ProtectedRoute";
import ActivitiesProtectedRoute from "./ActivitiesProtectedRoute";
import NotificationsProtectedRoute from "./NotificationsProtectedRoute";
import OwnerProtectedRoute from "./OwnerProtectedRoute";
import AnalyticsProtectedRoute from "./AnalyticsProtectedRoute";
import ReviewsProtectedRoute from "./ReviewsProtectedRoute";
import Home from "../pages/common/Home";
import Analytics from "../pages/analytics/Analytics";
import ExportUsers from "../pages/analytics/ExportUsers";
import Reviews from "../pages/reviews/Reviews";
import Administration from "../pages/administration/Administration";
import TextCreation from "../pages/create/TextCreation";
import PromotionalTextCreation from "../pages/create/PromotionalTextCreation";
import CartelCreation from "../pages/create/CartelCreation";
import NewsletterCreation from "../pages/create/NewsletterCreation";
import CartelRemix from "../pages/create/CartelRemix";
import NewsletterRemix from "../pages/create/NewsletterRemix";
import TextToSpeech from "../pages/create/TextToSpeech";
import Voices from "../pages/create/Voices";
import SoundLibrary from "../pages/create/SoundLibrary";
import SoundEffects from "../pages/create/SoundEffects";
import Translation from "../pages/create/Translation";
import CartelTranslation from "../pages/create/CartelTranslation";
import NewsletterTranslation from "../pages/create/NewsletterTranslation";
import Projects from "../pages/projects/Projects";
import AddItemToProject from "../pages/projects/AddExistingContentToProject";
import CartelProjects from "../pages/projects/CartelProjects";
import AddItemToCartelProject from "../pages/projects/AddItemToCartelProject";
import ModifyItemToCartelProject from "../pages/projects/ModifyItemToCartelProject";
import NewsletterProjects from "../pages/projects/NewsletterProjects";
import AddItemToNewsletterProject from "../pages/projects/AddItemToNewsletterProject";
import ModifyItemToNewsletterProject from "../pages/projects/ModifyItemToNewsletterProject";
import Login from "../pages/common/Login";
import Profile from "../pages/common/Profile";
import Register from "../pages/common/Register";
import Forgotten from "../pages/common/Forgotten";
import TrackCreationFlow from "../pages/track/create/TrackCreationFlow";
import Audiotracks from "../pages/track/manage/Audiotracks";
import ModifyItemToProject from "../pages/projects/ModifyContentProject";
import AudioDrafts from "../pages/drafts/AudioDrafts";
import AudioDraftEdit from "../pages/drafts/AudioDraftEdit";
import TextDrafts from "../pages/drafts/TextDrafts";
import TextDraftEdit from "../pages/drafts/TextDraftEdit";
import Trash from "../pages/trash/Trash";
import Documentation from "../pages/documentation/Documentation";
import Notification from "../pages/notification/Notification";
import { LoginProps } from "../types/screensTypes";
import Prompts from "../pages/administration/Prompts";
import AudioEditing from "../pages/create/AudioEditing";
import TextRemix from "../pages/create/TextRemix";
import MapStudio from "../pages/create/MapStudio";
import Database from "../pages/database/Database";
import Cities from "../pages/administration/Cities";
import Companies from "../pages/administration/Companies";
import Users from "../pages/administration/Users";
import AppUsers from "../pages/administration/AppUsers";
import VoicesAdmin from "../pages/administration/Voices";
import Activity from "../pages/activities/Activities";
import SoundLibraryEditable from "../pages/administration/SoundLibrary";
import Categories from "../pages/administration/Categories";
import BusinessCatalogs from "../pages/administration/BusinessCatalogs";
import Languages from "../pages/administration/Languages";
import WalkingTourSwitch from "../pages/administration/WalkingTourSwitch";
import Support from "../pages/support/Support";
import TrackListenedStories from "../pages/support/TrackListenedStories";
import TokenGeneration from "../pages/support/TokenGeneration";
import UsersSupp from "../pages/support/UserList";
import ReservationHistory from "../pages/support/ReservationHistory";
import QRCodeGeneration from "../pages/support/QRCodeGeneration";
import QueueAILogs from "../pages/support/QueueAILogs";
import AudioNormalization from "../pages/support/AudioNormalization/AudioNormalization";
import UsageDecline from "../pages/support/UsageDecline";
import HubCreate from "../pages/create/HubCreate";
import HubList from "../pages/create/HubList";
import HubFolders from "../pages/create/HubFolders";
import HubFolderCategories from "../pages/create/HubFolderCategories";
import SingleTrackCreation from "../pages/create/SingleTrackCreation";
import TrackCreationForm from "../pages/create/TrackCreationForm";
import ChatbotList from "../pages/chatbots/ChatbotList";
import BrandVoicesList from "../pages/resources/BrandVoicesList";
import CreateBrandVoice from "../pages/resources/CreateBrandVoice";
import AudienceProfilesList from "../pages/resources/AudienceProfilesList";
import AudioAnalysisTest from "../pages/administration/AudioAnalysisTest";
import ApiKeys from "../pages/administration/ApiKeys";

// **Define Props Interface**
interface AppRoutesProps {
    isLoggedIn: boolean;
    projects: { label: string; value: string }[];
    projectLinks: { name: string; path: string; logo: string }[];
    handleLogin: LoginProps["onLogin"];
    handleLogout: () => void;
    fetchProjects: () => Promise<void>;
}

const AppRoutes: React.FC<AppRoutesProps> = ({
    isLoggedIn,
    projects,
    projectLinks,
    handleLogin,
    handleLogout,
    fetchProjects,
}) => {
    const commonProps = { projectLinks, fetchProjects };

    return (
        <Routes>
            {/* 🔹 Authentication Routes */}
            <Route
                path="/login"
                element={
                    <LayoutWithSidebar>
                        {isLoggedIn ? <Navigate to="/" replace /> : <Login onLogin={handleLogin} />}
                    </LayoutWithSidebar>
                }
            />
            <Route
                path="/register"
                element={
                    <LayoutWithSidebar>
                        <Register setIsLoggedIn={() => handleLogin} />
                    </LayoutWithSidebar>
                }
            />
            <Route
                path="/forgotten-password"
                element={
                    <LayoutWithSidebar>
                        <Forgotten />
                    </LayoutWithSidebar>
                }
            />

            {/* 🔹 Dashboard & Analytics */}
            <Route
                path="/"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Home />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/analytics"
                element={
                    <AnalyticsProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Analytics />
                        </LayoutWithSidebar>
                    </AnalyticsProtectedRoute>
                }
            />
            <Route
                path="/analytics/export-users"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <ExportUsers />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/reviews"
                element={
                    <ReviewsProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Reviews />
                        </LayoutWithSidebar>
                    </ReviewsProtectedRoute>
                }
            />

            {/* 🔹 Database */}
            <Route
                path="/database"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Database />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/resources/brand-voices"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <BrandVoicesList />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/resources/brand-voices/create"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <CreateBrandVoice />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/resources/audience-profiles"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <AudienceProfilesList />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            {/* 🔹 Administration Routes */}
            {["/administration"].map((path) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute isLoggedIn={isLoggedIn}>
                            <LayoutWithSidebar adminMode {...commonProps}>
                                <Administration />
                            </LayoutWithSidebar>
                        </ProtectedRoute>
                    }
                />
            ))}

            <Route
                path="/administration/users"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <Users />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/app-users"
                element={
                    <OwnerProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <AppUsers />
                        </LayoutWithSidebar>
                    </OwnerProtectedRoute>
                }
            />

            <Route
                path="/administration/walking-tour-switch"
                element={
                    <OwnerProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <WalkingTourSwitch />
                        </LayoutWithSidebar>
                    </OwnerProtectedRoute>
                }
            />

            <Route
                path="/administration/cities"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <Cities />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/companies"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <Companies />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/prompts"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <Prompts />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/audio-analysis-test"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <AudioAnalysisTest />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/api-keys"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <ApiKeys />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/voices"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <VoicesAdmin />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/sounds"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <SoundLibraryEditable />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/categories"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <Categories />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/businessCatalogs"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <BusinessCatalogs />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/administration/languages"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar adminMode {...commonProps}>
                            <Languages />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Support />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support/users"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <UsersSupp />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support/track-stories"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <TrackListenedStories />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support/token-generation"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <TokenGeneration />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support/reservation-history"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <ReservationHistory />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support/generate-qr-code"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <QRCodeGeneration />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support/queue-ai-logs"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <QueueAILogs />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/support/audio-normalization"
                element={
                    <OwnerProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <AudioNormalization />
                        </LayoutWithSidebar>
                    </OwnerProtectedRoute>
                }
            />

            <Route
                path="/support/usage-decline"
                element={
                    <OwnerProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <UsageDecline />
                        </LayoutWithSidebar>
                    </OwnerProtectedRoute>
                }
            />

            {/* 🔹 Notifications & Documentation */}
            <Route
                path="/notification"
                element={
                    <NotificationsProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Notification />
                        </LayoutWithSidebar>
                    </NotificationsProtectedRoute>
                }
            />

            <Route
                path="/activity"
                element={
                    <ActivitiesProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Activity />
                        </LayoutWithSidebar>
                    </ActivitiesProtectedRoute>
                }
            />

            <Route
                path="/documentation"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Documentation />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            {/* 🔹 Content Creation Routes */}
            {[
                {
                    path: "/create/text/:id?",
                    component: <TextCreation refreshProjects={fetchProjects} />,
                    contentType: "text" as const,
                },
                {
                    path: "/create/promotional-text/:id?",
                    component: <PromotionalTextCreation refreshProjects={fetchProjects} />,
                    contentType: "text" as const,
                },
                {
                    path: "/create/text-remix/:id?",
                    component: <TextRemix refreshProjects={fetchProjects} />,
                    contentType: "text" as const,
                },
                {
                    path: "/create/cartel/:id?",
                    component: <CartelCreation refreshProjects={fetchProjects} />,
                    contentType: "cartel" as const,
                },
                {
                    path: "/create/cartel-remix/:id?",
                    component: <CartelRemix refreshProjects={fetchProjects} />,
                    contentType: "cartel" as const,
                },
                {
                    path: "/create/newsletter/:id?",
                    component: <NewsletterCreation refreshProjects={fetchProjects} />,
                    contentType: "newsletter" as const,
                },
                {
                    path: "/create/newsletter-remix/:id?",
                    component: <NewsletterRemix refreshProjects={fetchProjects} />,
                    contentType: "newsletter" as const,
                },
                {
                    path: "/create/text-to-speech/:id?",
                    component: <TextToSpeech refreshProjects={fetchProjects} />,
                    contentType: "text" as const,
                },
                { path: "/create/voices", component: <Voices /> },
                { path: "/create/sound-effects", component: <SoundEffects /> },
                { path: "/create/audio-editing", component: <AudioEditing /> },
                { path: "/create/sound-library", component: <SoundLibrary /> },
                { path: "/create/map-studio", component: <MapStudio /> },
                {
                    path: "/create/translation",
                    component: <Translation refreshProjects={fetchProjects} projects={projects} />,
                    contentType: "text" as const,
                },
                {
                    path: "/create/cartel-translation",
                    component: <CartelTranslation refreshProjects={fetchProjects} projects={projects} />,
                    contentType: "cartel" as const,
                },
                {
                    path: "/create/newsletter-translation",
                    component: <NewsletterTranslation refreshProjects={fetchProjects} projects={projects} />,
                    contentType: "newsletter" as const,
                },
                { path: "/hubs", component: <HubList /> },
                { path: "/hubs/create", component: <HubCreate /> },
                { path: "/hubs/folders", component: <HubFolders /> },
                { path: "/hubs/folders/categories", component: <HubFolderCategories /> },
                { path: "/hubs/folders/categories/tracks", component: <SingleTrackCreation /> },
                { path: "/hubs/folders/categories/tracks/create", component: <TrackCreationForm /> },
                { path: "/chatbots", component: <ChatbotList /> },
            ].map(({ path, component, contentType }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute isLoggedIn={isLoggedIn}>
                            <LayoutWithSidebar {...commonProps} contentType={contentType}>
                                {component}
                            </LayoutWithSidebar>
                        </ProtectedRoute>
                    }
                />
            ))}

            {/* 🔹 Project Management Routes */}
            {[
                {
                    path: "/projects/:id",
                    component: <Projects refreshProjects={fetchProjects} />,
                },
                {
                    path: "/projects/:id/add-item",
                    component: <AddItemToProject refreshProjects={fetchProjects} />,
                },
                {
                    path: "/projects/:id/update-item/:itemId",
                    component: <ModifyItemToProject refreshProjects={fetchProjects} />,
                },
            ].map(({ path, component }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute isLoggedIn={isLoggedIn}>
                            <LayoutWithSidebar {...commonProps}>{component}</LayoutWithSidebar>
                        </ProtectedRoute>
                    }
                />
            ))}

            {/* 🔹 Cartel Project Management Routes */}
            {[
                {
                    path: "/cartel-projects/:id",
                    component: <CartelProjects refreshProjects={fetchProjects} />,
                },
                {
                    path: "/cartel-projects/:id/add-item",
                    component: <AddItemToCartelProject refreshProjects={fetchProjects} />,
                },
                {
                    path: "/cartel-projects/:id/update-item/:itemId",
                    component: <ModifyItemToCartelProject refreshProjects={fetchProjects} />,
                },
            ].map(({ path, component }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute isLoggedIn={isLoggedIn}>
                            <LayoutWithSidebar {...commonProps} contentType="cartel">
                                {component}
                            </LayoutWithSidebar>
                        </ProtectedRoute>
                    }
                />
            ))}

            {/* 🔹 Newsletter Project Management Routes */}
            {[
                {
                    path: "/newsletter-projects/:id",
                    component: <NewsletterProjects refreshProjects={fetchProjects} />,
                },
                {
                    path: "/newsletter-projects/:id/add-item",
                    component: <AddItemToNewsletterProject refreshProjects={fetchProjects} />,
                },
                {
                    path: "/newsletter-projects/:id/update-item/:itemId",
                    component: <ModifyItemToNewsletterProject refreshProjects={fetchProjects} />,
                },
            ].map(({ path, component }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute isLoggedIn={isLoggedIn}>
                            <LayoutWithSidebar {...commonProps} contentType="newsletter">
                                {component}
                            </LayoutWithSidebar>
                        </ProtectedRoute>
                    }
                />
            ))}

            {/* 🔹 Drafts (Brouillon) - content not assigned to a project, owner only */}
            <Route
                path="/create/drafts"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <AudioDrafts />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/create/drafts/update-item/:itemId"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <AudioDraftEdit />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            {[
                { path: "/create/cartel-drafts", component: <TextDrafts itemType="cartel" /> },
                {
                    path: "/create/cartel-drafts/update-item/:itemId",
                    component: <TextDraftEdit itemType="cartel" />,
                },
            ].map(({ path, component }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute isLoggedIn={isLoggedIn}>
                            <LayoutWithSidebar {...commonProps} contentType="cartel">
                                {component}
                            </LayoutWithSidebar>
                        </ProtectedRoute>
                    }
                />
            ))}
            {[
                { path: "/create/newsletter-drafts", component: <TextDrafts itemType="newsletter" /> },
                {
                    path: "/create/newsletter-drafts/update-item/:itemId",
                    component: <TextDraftEdit itemType="newsletter" />,
                },
            ].map(({ path, component }) => (
                <Route
                    key={path}
                    path={path}
                    element={
                        <ProtectedRoute isLoggedIn={isLoggedIn}>
                            <LayoutWithSidebar {...commonProps} contentType="newsletter">
                                {component}
                            </LayoutWithSidebar>
                        </ProtectedRoute>
                    }
                />
            ))}

            {/* 🔹 Trash (Corbeille) - soft-deleted content & projects, owner only */}
            <Route
                path="/create/trash"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps}>
                            <Trash itemType="audio" refreshProjects={fetchProjects} />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/create/cartel-trash"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps} contentType="cartel">
                            <Trash itemType="cartel" refreshProjects={fetchProjects} />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/create/newsletter-trash"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar {...commonProps} contentType="newsletter">
                            <Trash itemType="newsletter" refreshProjects={fetchProjects} />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            {/* 🔹 User Profile */}
            <Route
                path="/profile"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar>
                            <Profile handleLogout={handleLogout} />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            {/* 🔹 Audio Track Management */}
            <Route
                path="/manage/audiotracks"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar>
                            <Audiotracks />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />

            {/* 🔹 Track Creation Flow */}
            <Route
                path="/create/tour/:trackId/*"
                element={
                    <ProtectedRoute isLoggedIn={isLoggedIn}>
                        <LayoutWithSidebar>
                            <TrackCreationFlow />
                        </LayoutWithSidebar>
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
};

export default AppRoutes;
